/**
 * Extracts institution and organization IDs from a URL hash
 * @param url The URL hash to extract IDs from (e.g. "#/account/institution/123/overview" or "#/account/organization/456/settings")
 * @returns Discriminated union based on the route type detected
 */
export const extractIdsFromUrl = (url) => {
    if (!url) {
        return { type: 'unknown' };
    }
    // Clean the URL by removing the hash prefix if present
    const cleanUrl = url.startsWith('#') ? url.substring(1) : url;
    // 1. Settings route (mutually exclusive with IDs)
    // Detect settings route: /account/settings
    // The settings page doesn't have institutionId in the URL path.
    // The caller should look up institutionId from localStorage when this is true.
    if (/\/account\/settings(?:\/|$|\?)/.test(cleanUrl)) {
        return { type: 'settings' };
    }
    // Extract childId from child profile route: /account/childProfile/:childId
    // - "/account/childProfile/" matches the literal path prefix
    // - ([^/?]+) captures the child ID, stopping at either '/' or '?' characters
    // Examples:
    // - From "/account/childProfile/abc-123/activity" captures "abc-123"
    // - From "/account/childProfile/def-456?tab=overview" captures "def-456"
    const childProfileMatch = cleanUrl.match(/\/account\/childProfile\/([^/?]+)/);
    const childId = childProfileMatch === null || childProfileMatch === void 0 ? void 0 : childProfileMatch[1];
    // 2. Child profile route
    if (childId) {
        return {
            type: 'childProfile',
            childId,
        };
    }
    // Extract institution ID:
    // - "/account/institution/" matches the literal path prefix
    // - ([^/?]+) captures one or more characters that are not forward slashes or question marks
    // Examples:
    // - From "/account/institution/abc-123/overview" captures "abc-123"
    // - From "/account/institution/def-456?param=value" captures "def-456"
    const institutionMatch = cleanUrl.match(/\/account\/institution\/([^/?]+)/);
    const institutionId = institutionMatch === null || institutionMatch === void 0 ? void 0 : institutionMatch[1];
    // 3. Institution route
    if (institutionId) {
        return {
            type: 'institution',
            institutionId,
        };
    }
    // Extract organization ID:
    // - "/account/organization/" matches the literal path prefix
    // - ([^/?]+) captures the ID, stopping at either '/' or '?' characters
    // Examples:
    // - From "/account/organization/xyz-789/settings" captures "xyz-789"
    // - From "/account/organization/uvw-012?tab=overview" captures "uvw-012"
    const organizationMatch = cleanUrl.match(/\/account\/organization\/([^/?]+)/);
    const organizationId = organizationMatch === null || organizationMatch === void 0 ? void 0 : organizationMatch[1];
    // 4. Organization route
    if (organizationId) {
        return {
            type: 'organization',
            organizationId,
        };
    }
    // 5. Unknown route (no IDs)
    return { type: 'unknown' };
};
